import { supabase } from '../lib/supabase';

/**
 * Real-time Service for LocalProCRM
 * Manages Supabase real-time subscriptions with tenant isolation
 * Provides live data synchronization across all entities
 */

class RealtimeService {
  constructor() {
    this.channels = new Map();
    this.subscriptions = new Map();
  }

  /**
   * Subscribe to entity changes with tenant isolation
   * @param {string} entity - Entity name (contacts, deals, customers, etc.)
   * @param {string} tenantId - Tenant ID for isolation
   * @param {function} onInsert - Callback for INSERT events
   * @param {function} onUpdate - Callback for UPDATE events
   * @param {function} onDelete - Callback for DELETE events
   * @returns {function} Unsubscribe function
   */
  subscribeToEntity(entity, tenantId, { onInsert, onUpdate, onDelete }) {
    if (!tenantId) {
      console.warn(`Cannot subscribe to ${entity}: tenantId is required`);
      return () => {};
    }

    const channelName = `realtime:tenant:${tenantId}:${entity}`;
    
    // Check if channel already exists
    if (this.channels?.has(channelName)) {
      console.log(`Channel ${channelName} already exists, reusing...`);
      return this.getUnsubscribeFunction(channelName);
    }

    console.log(`Creating real-time channel: ${channelName}`);

    // Create new channel
    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: this.getTableName(entity),
          filter: `tenant_id=eq.${tenantId}`
        },
        (payload) => {
          console.log(`[${entity}] INSERT:`, payload?.new);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: this.getTableName(entity),
          filter: `tenant_id=eq.${tenantId}`
        },
        (payload) => {
          console.log(`[${entity}] UPDATE:`, payload?.new);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: this.getTableName(entity),
          filter: `tenant_id=eq.${tenantId}`
        },
        (payload) => {
          console.log(`[${entity}] DELETE:`, payload?.old);
          if (onDelete) onDelete(payload?.old);
        }
      )?.subscribe((status) => {
        console.log(`Channel ${channelName} status:`, status);
      });

    this.channels?.set(channelName, channel);
    return this.getUnsubscribeFunction(channelName);
  }

  /**
   * Subscribe to user-specific notifications
   * @param {string} userId - User ID
   * @param {function} onNotification - Callback for new notifications
   * @returns {function} Unsubscribe function
   */
  subscribeToUserNotifications(userId, onNotification) {
    if (!userId) {
      console.warn('Cannot subscribe to notifications: userId is required');
      return () => {};
    }

    const channelName = `realtime:user:${userId}:notifications`;

    if (this.channels?.has(channelName)) {
      return this.getUnsubscribeFunction(channelName);
    }

    console.log(`Creating notification channel: ${channelName}`);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_logs',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          console.log('[Notification] New:', payload?.new);
          if (onNotification) onNotification(payload?.new);
        }
      )?.subscribe((status) => {
        console.log(`Notification channel status:`, status);
      });

    this.channels?.set(channelName, channel);
    return this.getUnsubscribeFunction(channelName);
  }

  /**
   * Subscribe to multiple entities at once
   * @param {Array} entities - Array of {entity, callbacks} objects
   * @param {string} tenantId - Tenant ID
   * @returns {function} Unsubscribe all function
   */
  subscribeToMultipleEntities(entities, tenantId) {
    const unsubscribeFunctions = entities?.map(({ entity, callbacks }) =>
      this.subscribeToEntity(entity, tenantId, callbacks)
    );

    return () => {
      unsubscribeFunctions?.forEach(unsub => unsub());
    };
  }

  /**
   * Unsubscribe from specific channel
   * @param {string} channelName - Channel name to unsubscribe
   */
  unsubscribe(channelName) {
    const channel = this.channels?.get(channelName);
    if (channel) {
      console.log(`Unsubscribing from channel: ${channelName}`);
      supabase?.removeChannel(channel);
      this.channels?.delete(channelName);
    }
  }

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    console.log('Unsubscribing from all channels...');
    this.channels?.forEach((channel, channelName) => {
      supabase?.removeChannel(channel);
    });
    this.channels?.clear();
  }

  /**
   * Get unsubscribe function for a channel
   * @param {string} channelName - Channel name
   * @returns {function} Unsubscribe function
   */
  getUnsubscribeFunction(channelName) {
    return () => this.unsubscribe(channelName);
  }

  /**
   * Map entity name to database table name
   * @param {string} entity - Entity name
   * @returns {string} Table name
   */
  getTableName(entity) {
    const tableMap = {
      contacts: 'customers',
      customers: 'customers',
      deals: 'deals',
      appointments: 'appointments',
      activities: 'activities',
      services: 'services',
      organizations: 'tenants',
      invoices: 'invoices',
      users: 'user_profiles'
    };

    return tableMap?.[entity] || entity;
  }

  /**
   * Get active channels count
   * @returns {number} Number of active channels
   */
  getActiveChannelsCount() {
    return this.channels?.size;
  }

  /**
   * Get all active channel names
   * @returns {Array<string>} Array of channel names
   */
  getActiveChannels() {
    return Array.from(this.channels?.keys());
  }
}

// Export singleton instance
export const realtimeService = new RealtimeService();
export default realtimeService;
